<template>
  <div
    v-if="isPreOrder"
    class="pre-order-message"
  >
    <div class="pre-order-message__container">
      <icon-info />

      <span
        v-if="hasPreOrderTag && !product?.metafields?.preOrderAvailableDate"
        class="pre-order-message__message text-body-xs-regular"
        v-html="`${preOrderClosingMessage} ${genericMessage}`"
      />

      <span
        v-else-if="isPreOrderEnded"
        class="pre-order-message__message text-body-xs-regular"
        v-html="`${preOrderEndedClosingMessage} ${preOrderEndedAvailableMessage}`"
      />

      <span
        v-else-if="isPreOrderAvailable || isPreOrderClosing"
        class="pre-order-message__message text-body-xs-regular"
        v-html="`${preOrderClosingMessage} ${preOrderAvailableMessage}`"
      />
    </div>
  </div>

  <div v-else-if="hasAvailableToBuyDate">
    <div class="pre-order-message__container">
      <icon-info />

      <span
        class="pre-order-message__message text-body-xs-regular"
        v-html="`${availableToBuyMessage}`"
      />
    </div>
  </div>
</template>

<script>

/**
 * Vue: Pre order message (pre-order-message)
 * -----------------------------------------------------------------------------
 * Show pre-order message.
 * - Component settings: snippet / dynamic / async / scroll
 *
 * @param {Boolean|Object} product - Canvas formatted product object.
 * @param {Boolean|Null|Object} variant - Canvas formatted variant object.
 *
 */
import { handlePreOrderProductCurrentDate, handlePreOrderProductFutureDate, handlePreOrderProductFutureDateTime, handlePreOrderProductPastDate } from '~/helpers/general'
import IconInfo from '~icons/general/info.svg'

export default {
  name: 'PreOrderMessage',

  components: {
    IconInfo,
  },

  props: {
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    variant: {
      type: [Boolean, null, Object],
      default: false,
    },
  },

  data() {
    return {

    }
  },

  computed: {

    /**
     * Is pre-order tag.
     */
    hasPreOrderTag() {
      return this.product.tags?.some((tag) => tag.includes('trade-pre-order'))
    },

    /**
     * Is pre-order.
     */
    isPreOrder() {
      // Calculate extended date when closing date is met
      if (this.hasPreOrderTag && this.isPreOrderEnded) {
        if (!this.product?.metafields?.preOrderAvailableDate) {
          return true
        }

        const availableDate = new Date(this.product?.metafields?.preOrderAvailableDate)
        const orderDatePlusVariable = parseInt(this.$string('product.pre_order.order_date_plus_variable'))
        const extendedDate = new Date(availableDate)

        if (!isNaN(orderDatePlusVariable)) {
          extendedDate.setDate(extendedDate.getDate() + orderDatePlusVariable)
        }

        // Show pre-order ended until extended date is reached
        if (
          handlePreOrderProductCurrentDate(extendedDate) ||
          handlePreOrderProductPastDate(extendedDate)
        ) {
          return false
        }

        return true
      }

      if (
        (this.hasPreOrderTag &&
          handlePreOrderProductCurrentDate(this.product?.metafields?.preOrderAvailableDate)) ||
        (this.hasPreOrderTag &&
          handlePreOrderProductPastDate(this.product?.metafields?.preOrderAvailableDate))
      ) {
        return false
      }

      return this.hasPreOrderTag
    },

    /**
     * Is pre-order available.
     */
    isPreOrderAvailable() {
      return handlePreOrderProductFutureDate(this.product?.metafields?.preOrderAvailableDate)
    },

    /**
     * Is pre-order closing.
     */
    isPreOrderClosing() {
      return handlePreOrderProductFutureDate(this.product?.metafields?.preOrderClosingDate)
    },

    /**
     * Is pre-order ended.
     */
    isPreOrderEnded() {
      return handlePreOrderProductCurrentDate(this.product?.metafields?.preOrderClosingDate) ||
        handlePreOrderProductPastDate(this.product?.metafields?.preOrderClosingDate)
    },

    /**
     * Is pre-order available message.
     */
    isPreOrderAvailableMessage() {
      return this.product?.metafields?.preOrderAvailableMessage
    },

    /**
     * Is pre-order closing message.
     */
    isPreOrderClosingMessage() {
      return this.product?.metafields?.preOrderClosingMessage
    },

    /**
     * Pre-order available message.
     */
    preOrderAvailableMessage() {
      if (this.isPreOrderAvailable) {
        let dateToShow = new Date(this.product?.metafields?.preOrderAvailableDate)
        const originalDate = new Date(this.product?.metafields?.preOrderAvailableDate)

        // If closing date not met, subtract ship_date_minus_variable
        if (this.isPreOrderClosing) {
          const shipDateMinusVariable = parseInt(this.$string('product.pre_order.ship_date_minus_variable'))
          if (!isNaN(shipDateMinusVariable) && shipDateMinusVariable > 0) {
            dateToShow = new Date(dateToShow)
            dateToShow.setDate(dateToShow.getDate() - shipDateMinusVariable)

            // If calculated date is in the past, use original available date
            if (handlePreOrderProductPastDate(dateToShow)) {
              dateToShow = originalDate
            }
          }
        }

        const formatDate = `<b>${this.$formatDate('date_formats.day_singular_full_month_year', dateToShow)}</b>`

        if (this.isPreOrderAvailableMessage) {
          return `${this.isPreOrderAvailableMessage} ${formatDate}.`
        }

        return this.$string(
          'product.pre_order.generic_available_message_date_html',
          {
            replace: {
              date: formatDate,
            },
          },
        )
      }

      return ''
    },

    /**
     * Pre-order closing message.
     */
    preOrderClosingMessage() {
      if (this.isPreOrderClosing) {
        const formatDate = `<b>${this.$formatDate('date_formats.day_singular_full_month_year', new Date(this.product?.metafields?.preOrderClosingDate))}</b>`

        if (this.isPreOrderClosingMessage) {
          return `${this.isPreOrderClosingMessage} ${formatDate}.`
        }

        return this.$string(
          'product.pre_order.generic_closing_message_date_html',
          {
            replace: {
              date: formatDate,
            },
          },
        )
      }

      return ''
    },

    /**
     * Pre-order ended available message.
     */
    preOrderEndedAvailableMessage() {
      const dateToShow = new Date(this.product?.metafields?.preOrderAvailableDate)

      // Add order_date_plus_variable to available date when closing date is met
      const orderDatePlusVariable = parseInt(this.$string('product.pre_order.order_date_plus_variable'))

      if (!isNaN(orderDatePlusVariable)) {
        dateToShow.setDate(dateToShow.getDate() + orderDatePlusVariable)
      }

      const formatDate = `<b>${this.$formatDate('date_formats.day_singular_full_month_year', dateToShow)}</b>`

      return this.$string(
        'product.pre_order.generic_ended_available_message_date_html',
        {
          replace: {
            date: formatDate,
          },
        },
      )
    },

    /**
     * Pre-order ended closing message.
     */
    preOrderEndedClosingMessage() {
      const formatDate = `<b>${this.$formatDate('date_formats.day_singular_full_month_year', new Date(this.product?.metafields?.preOrderClosingDate))}</b>`

      return this.$string(
        'product.pre_order.generic_ended_closing_message_date_html',
        {
          replace: {
            date: formatDate,
          },
        },
      )
    },

    /**
     * Generic message string.
     */
    genericMessage() {
      return this.$string('product.pre_order.generic_default_message_html')
    },

    /**
     * Available to buy date.
     * - Check if 'availableToBuyDate' date is in the future.
     */
    hasAvailableToBuyDate() {
      return this.product?.metafields?.availableToBuyDate &&
        // eslint-disable-next-line max-len
        handlePreOrderProductFutureDateTime(this.product?.metafields?.availableToBuyDate, this.product?.currentDateTime) &&
        this.variant?.available
    },

    /**
     * Available to buy message.
     */
    availableToBuyMessage() {
      const formatDate = `<b>${this.$formatDate('date_formats.day_singular_full_month_year', new Date(this.product?.metafields?.availableToBuyDate))}</b>`
      const dateCurrent = new Date(this.product?.metafields?.availableToBuyDate)
      const hour = dateCurrent.getHours().toString().padStart(2, '0')
      const minute = dateCurrent.getMinutes().toString().padStart(2, '0')
      const formatTime = `<b>${hour}:${minute}</b>`
      return this.$string(
        'product.pre_order.generic_available_to_buy_message_date_html',
        {
          replace: {
            time: formatTime,
            date: formatDate,
          },
        },
      )
    },
  },
}
</script>

<style lang="scss">
@import './pre-order-message';
</style>
